import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import React, { useEffect, useState } from 'react';
import Loading from '../../../components/Loading';
import {
    Edit, Trash2, History, Plus, ShoppingBag, ChevronRight, Home,
    CheckCircle, XCircle, ImageOff,
} from 'lucide-react';
import {
    fetchProductosCafeteria,
    ProductoCafeteria,
} from '../../../services/productoCafeteriaService';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

import AddProducts from '../../../components/Products/AddProducts';
import EditProducts from '../../../components/Products/EditProducts';
import DeleteProducts from '../../../components/Products/DeleteProducts';

const C = {
    primary: 'var(--ad-primary, #3564db)',
    primaryDark: 'var(--ad-primary-dark, #8f1f64)',
    secondary: 'var(--ad-secondary, #3564db)',
    secondaryDark: '#004d7a',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark: '#ea8406',
};

const css = `

.pp-wrap * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes ppFadeUp  { from{opacity:0;transform:translateY(14px);}to{opacity:1;transform:translateY(0);} }
@keyframes ppBlob    { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;} }
@keyframes spin { to{transform:rotate(360deg);} }
.pp-fadeup   { animation:ppFadeUp .45s cubic-bezier(.16,1,.3,1) both; }
.pp-fadeup-1 { animation:ppFadeUp .45s .08s cubic-bezier(.16,1,.3,1) both; }
.pp-card { background:var(--ad-surface, #fff);border-radius:20px;border:1.5px solid var(--ad-line, #f1f5f9);box-shadow:0 2px 12px rgba(0,0,0,.04);overflow:hidden; }
.pp-table-row { transition:background .15s; }
.pp-table-row:hover { background:var(--ad-soft, #f8fafc); }
.pp-action-btn { width:30px;height:30px;border-radius:9px;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s; }
.pp-action-btn.edit { background:color-mix(in srgb, ${C.secondary} 6.27%, transparent);color:var(--ad-link, #2854c5); }
.pp-action-btn.edit:hover { background:color-mix(in srgb, ${C.secondary} 12.55%, transparent);transform:translateY(-1px); }
.pp-action-btn.del  { background:color-mix(in srgb, ${C.primary} 6.27%, transparent);color:var(--ad-link, #2854c5); }
.pp-action-btn.del:hover  { background:color-mix(in srgb, ${C.primary} 12.55%, transparent);transform:translateY(-1px); }
.pp-scroll::-webkit-scrollbar{width:5px;height:5px;}
.pp-scroll::-webkit-scrollbar-track{background:transparent;}
.pp-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.25);border-radius:10px;}
.pp-img-preview { width:28px;height:28px;border-radius:6px;object-fit:cover;border:1.5px solid var(--ad-line, #f1f5f9); }
`;



const ProductsPage: React.FC = () => {
    const [products, setProducts] = useState<ProductoCafeteria[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductoCafeteria | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState<ProductoCafeteria | null>(null);

    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('productocafeteria', userLevelId); // ajusta según tu backend

    useEffect(() => {
        if (!perm) return;
        if (!perm.can_read) {
            setProducts([]);
            setLoadingProducts(false);
            return;
        }
        let active = true;
        setLoadingProducts(true);
        fetchProductosCafeteria()
            .then(data => {
                if (active) setProducts(data.sort((a, b) => a.nombre.localeCompare(b.nombre)));
            })
            .catch(() => {
                if (active) setProducts([]);
            })
            .finally(() => {
                if (active) setLoadingProducts(false);
            });
        return () => { active = false; };
    }, [perm]);

    const handleCreated = (p: ProductoCafeteria) => setProducts(prev => [...prev, p]);
    const handleUpdated = (p: ProductoCafeteria) =>
        setProducts(prev => prev.map(x => (x.id === p.id ? p : x)));
    const handleDeleted = (id: number) => setProducts(prev => prev.filter(x => x.id !== id));
    const handleDeleteClick = (p: ProductoCafeteria) => {
        setProductToDelete(p);
        setIsDeleteOpen(true);
    };

    const totalPages = Math.ceil(products.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedProducts = products.slice(startIndex, startIndex + itemsPerPage);

    if (perm === null || loadingProducts) return <Loading text="Cargando productos..." />;

    return <><LumaResourceWorkspace title="Productos" description="Catálogo de productos de cafetería" group="Productos" rows={products} columns={[{key:'nombre',label:'Nombre'},{key:'categoria_info',label:'Categoría',render:r=>r.categoria_info?.nombre||'Sin categoría'},{key:'precio',label:'Precio',render:r=>lumaMoney(r.precio)},{key:'stock',label:'Stock'},{key:'activo',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="activo" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsAddOpen(true)}:undefined} onEdit={perm?.can_update?r=>{setSelectedProduct(r);setIsEditOpen(true);}:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history=""/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async () => { setIsSessionExpired(false); await logout(); }} /><AddProducts isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onCreated={handleCreated} /><EditProducts isOpen={isEditOpen} onClose={() => { setIsEditOpen(false); setSelectedProduct(null); }} initialData={selectedProduct} onUpdate={handleUpdated} /><DeleteProducts isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} productId={productToDelete?.id ?? null} productName={productToDelete?.nombre ?? ''} onDeleted={handleDeleted} /></>;
};

export default ProductsPage;
